import { rem, useToasts } from "@certn/cascadia";
import {
  Container,
  Grid,
  GridItem,
  Link,
  PrimaryButton,
  SummaryCard,
  Text,
} from "@certn/cascadia-react";
import { Checkbox } from "@certn/cascadia-react/deprecated/inputs/checkbox/Checkbox";
import { APIError, useAPIRequest } from "@certn/legacy-api";
import { slugify, useGoogleAnalytics } from "@certn/utils";
import { css } from "@emotion/css";
import * as Sentry from "@sentry/browser";
import { useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useTimeout } from "usehooks-ts";

import { NotStarted } from "./NotStarted";
import { getCurrentAddressFromSections } from "./utils";
import { Section } from "../Application.context";
import { customSections } from "../Section";
import { getField } from "../fields";
import { useApplicationSections } from "../hooks";

export function Summary({ sections }: { sections: Section[] }) {
  const { mutate } = useApplicationSections();
  useTimeout(mutate, 0);

  const { applicantId } = useParams();
  const navigate = useNavigate();
  const request = useAPIRequest();
  const { show } = useToasts();
  const { t } = useTranslation();
  const currentAddress = useMemo(
    () => getCurrentAddressFromSections(sections),
    [sections]
  );
  const shouldShowConsumerReportCheckbox =
    currentAddress &&
    currentAddress.country === "US" &&
    ["CA", "MN", "OK"].includes(currentAddress.province_state);
  const [requestCopy, setRequestCopy] = useState<boolean | undefined>(
    shouldShowConsumerReportCheckbox ? false : undefined
  );
  const [isSubmitting, setIsSubmitting] = useState<boolean>();
  const [showErrors, setShowErrors] = useState<boolean>(false);
  const scrollToRef = useRef<HTMLDivElement>(null);
  const { gaTrackEvent } = useGoogleAnalytics();

  const handleSubmitInformation = async () => {
    setIsSubmitting(true);

    try {
      const sectionIncomplete = sections.some(
        (section) => !section.isCompleted
      );

      if (sectionIncomplete) {
        setShowErrors(true);
        scrollToRef.current?.scrollIntoView({ behavior: "smooth" });
        return setIsSubmitting(false);
      }

      await request(`/internal/people/applicant/${applicantId}/submit/`, {
        method: "POST",
        body: JSON.stringify({ requested_disclosure_email: requestCopy }),
      });
      gaTrackEvent("application_submit");
      navigate("/", { state: { showSuccessModal: true } });
    } catch (e) {
      if (
        e instanceof APIError &&
        e.detail === "Application has already been submitted."
      ) {
        show({
          title: t("Application already submitted"),
          type: "error",
        });
      } else {
        show({
          title: t(
            "Something went wrong, try again or contact the Certn support team at support@certn.co"
          ),
          type: "error",
        });

        if (e instanceof APIError) {
          Sentry.captureException(
            `API error on submit: ${e.message}: ${JSON.stringify(e.errors)}`
          );
        } else {
          Sentry.captureException(
            `Unknown error on submit: ${(e as Error).message}`
          );
        }
      }
    }
    setIsSubmitting(false);
  };

  return (
    <Container>
      <Grid>
        <GridItem alignItems="left">
          <Text as="h1" style="form/page-title" testID="summary-section-header">
            <Trans>Review & Submit</Trans>
          </Text>
          <Text
            style="form/body-primary"
            color="text-weak"
            className={css({ marginTop: rem("space-2") })}
            testID="summary-section-description"
          >
            <Trans>
              Review the information you've provided us and confirm that it is
              correct. Once you submit your details, you won't be able to edit
              again.
            </Trans>
          </Text>
        </GridItem>

        {shouldShowConsumerReportCheckbox && (
          <GridItem>
            <Checkbox
              description={
                <Trans>
                  For a paper copy, contact Certn at +1 (844) 987-0690 or{" "}
                  <Link
                    textStyle="global/body-secondary"
                    textColor="text-weak"
                    testID="support-link"
                  >
                    support@certn.co
                  </Link>
                </Trans>
              }
              onChange={setRequestCopy}
            >
              <Trans>
                I want to receive a copy of my Consumer Report, Investigate
                Consumer Report or Credit Report from Certn electronically.
              </Trans>
            </Checkbox>
          </GridItem>
        )}

        <GridItem>
          <PrimaryButton
            testID="review-and-submit-submit-information-button"
            fullWidth
            type="submit"
            size="medium"
            isDisabled={isSubmitting}
            isLoading={isSubmitting}
            onPress={handleSubmitInformation}
          >
            <Trans>Submit information</Trans>
          </PrimaryButton>
        </GridItem>

        {sections
          .filter(({ key }) => !customSections.includes(key))
          .map((section) => {
            return (
              <GridItem
                key={section.key}
                aria-label={`${t("Summary")} - ${section.title}`}
              >
                <SummaryCard
                  key={section.key}
                  header={section.title ?? ""}
                  actions={
                    <Link
                      linkStyle="monochrome"
                      href={`/apply/${applicantId}/${section.key}`}
                      textStyle="global/body-secondary"
                      testID={`${section.key}-edit-button`}
                    >
                      <Trans>Edit</Trans>
                    </Link>
                  }
                  testID={`summary-section-${slugify(section.key)}-summary`}
                >
                  {section.isCompleted ? (
                    section.fields.map((field) =>
                      getField(field)?.summaryElement?.(
                        `/apply/${applicantId}/${section.key}`
                      )
                    )
                  ) : (
                    <NotStarted
                      scrollToRef={scrollToRef}
                      sectionKey={section.key}
                      showErrors={showErrors}
                    />
                  )}
                </SummaryCard>
              </GridItem>
            );
          })}
      </Grid>
    </Container>
  );
}
